import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, MessageCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function PatientsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });

  const load = async () => {
    try { const r = await api.get("/patients"); setItems(r.data); } catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Nombre y teléfono requeridos"); return; }
    try {
      const r = await api.post("/patients", form);
      setItems([r.data, ...items]);
      setForm({ name: "", phone: "", notes: "" });
      toast.success("Paciente añadido");
    } catch { toast.error("Error"); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este paciente?")) return;
    await api.delete(`/patients/${id}`);
    setItems(items.filter((p) => p.id !== id));
  };

  const wa = (phone, name) => {
    const clean = String(phone).replace(/\D/g, "");
    const prefixed = clean.startsWith("52") ? clean : `52${clean}`;
    return `https://wa.me/${prefixed}?text=${encodeURIComponent(`Hola ${name}, te saluda la clínica de la Dra. Tania Mendoza.`)}`;
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="patients-panel">
      <form onSubmit={submit} className="lg:col-span-4 bg-white rounded-2xl border border-[#E6E2DC] p-7 space-y-5 h-fit" data-testid="patient-form">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Nuevo paciente</div>
          <h3 className="font-display text-2xl text-[#1A1C1A]">Añadir contacto</h3>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Nombre</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="patient-name" />
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">WhatsApp (10 dígitos)</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="7712345678" required data-testid="patient-phone" />
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Notas</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="patient-notes" />
        </div>
        <Button type="submit" className="w-full rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 text-xs uppercase tracking-[0.2em]" data-testid="patient-submit">
          <UserPlus className="w-4 h-4 mr-2" /> Añadir
        </Button>
      </form>

      <div className="lg:col-span-8">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-3">Pacientes ({items.length})</div>
        <div className="bg-white rounded-2xl border border-[#E6E2DC] overflow-hidden">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#5C5E5C]" data-testid="patients-empty">Aún no hay pacientes registrados.</div>
          ) : (
            <ul className="divide-y divide-[#E6E2DC]">
              {items.map((p) => (
                <li key={p.id} className="p-5 flex items-center justify-between" data-testid={`patient-${p.id}`}>
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#5C5E5C]">{p.phone}{p.notes ? ` · ${p.notes}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={wa(p.phone, p.name)} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-full text-xs border-[#7A9180] text-[#3a5a44] hover:bg-[#E6F0E8]">
                        <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                      </Button>
                    </a>
                    <button onClick={() => remove(p.id)} className="p-2 text-[#965D5D] hover:bg-[#F5E0E0] rounded-lg" data-testid={`patient-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
